import { joinUrl } from "../utils/utils"
import { apiHost } from "../constants/apiHost"
import * as Sentry from "@sentry/react"
import React from "react"

export async function boundApiRequest<T>(
  path: string,
  body?: Record<string, any>,
): Promise<T | { error: string }> {
  const url = joinUrl(apiHost, path)

  const headers: any = {}

  headers["X-Skeem-Auth-Token"] = await window.skeem.getAuthToken()

  try {
    console.log("fetching", url, body)
    const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) })
    const responseText = await response.text()

    try {
      const json = JSON.parse(responseText)

      if (!response.ok) {
        let message = "There was a problem connecting to the API server (not OK)"

        if (typeof json === "object" && json && "error" in json) {
          if ("message" in json.error && typeof json.error.message === "string") {
            message = json.error.message
          } else {
            message = typeof json.error === "string" ? json.error : JSON.stringify(json.error)
          }
        }
        return { error: message }
      }

      return json
    } catch {
      console.error("error in api request", url, body, responseText)
      return { error: "There was a problem connecting to the API server (invalid JSON)" }
    }
  } catch (e) {
    Sentry.captureException(e)
    return { error: "There was a problem connecting to the API server" }
  }
}

export function useFetchData<T>(
  fetchFunction: () => Promise<T | { error: string }>,
  dependency: any,
): { data: T | null; error: Error | null; loading: boolean } {
  const [data, setData] = React.useState<T | null>(null)
  const [error, setError] = React.useState<Error | null>(null)
  const [loading, setLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const result = await fetchFunction()
        if ("error" in result) {
          setError(new Error(result.error))
        } else {
          setData(result)
        }
      } catch (err) {
        setError(err as Error)
      } finally {
        setLoading(false)
      }
    }

    if (dependency) {
      fetchData()
    }
  }, [dependency])

  return { data, error, loading }
}
